import { useState, useCallback, useRef, FC, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { AddressAutofill } from "@mapbox/search-js-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActivePanelType } from "../types";

const API_URL = import.meta.env.VITE_API_URL;

import {
  Map as MapboxMap,
  Source,
  Layer,
  MapRef,
  Marker,
} from "react-map-gl/mapbox";
import type { MapMouseEvent, MarkerDragEvent } from "react-map-gl/mapbox";

import type { SkyLayerSpecification } from "react-map-gl/mapbox";

const skyLayer: SkyLayerSpecification = {
  id: "sky",
  type: "sky",
  paint: {
    "sky-type": "atmosphere",
    "sky-atmosphere-sun": [0.0, 0.0],
    "sky-atmosphere-sun-intensity": 15,
  },
};

import { ProjectData, PinLocation } from "../types";
import Header from "../Header";
import Footer from "../Footer";
import { clearMessages } from "../../utils/errorHandling";
import {
  transformBackendToFrontend,
  transformFrontendToBackend,
} from "../../utils/dataTransformation";

import "mapbox-gl/dist/mapbox-gl.css";
import "./PropertyMap.css";
import { SidePanel } from "./side_panel/sidePanel";

import { useBoundaryLogic } from "./hooks/useBoundaryLogic";
import { useObjectLogic } from "./hooks/useObjectLogic";
import { useMapViewportLogic } from "./hooks/useMapViewport";

import { BoundaryMarkers } from "./components/BoundaryMarkers";
import { BoundaryPolygon } from "./components/BoundaryPolygons";
import { ObjectMarkers } from "./components/ObjectMarkers";
import { apiClient } from "@/utils/apiClient/apiClient";

const PropertyMap: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();

  const [projectData, setProjectData] = useState<ProjectData | null>(null);

  const mapRef = useRef<MapRef | null>(null);

  // Add editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [editingAddress, setEditingAddress] = useState("");

  // Add state to track original saved values (for comparison)
  const [lastSaveProject, setLastSaveProject] = useState<ProjectData | null>(
    null
  );

  const [confirmMessage, setConfirmMessage] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Add loading state for project data
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const isLoadingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  // Add state for saving map view
  const [saveMapView, setSaveMapView] = useState(true);

  const [activePanel, setActivePanel] = useState<ActivePanelType>("boundary");

  // Add state for company information
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    company_id: string;
  } | null>(null);

  // State for tracking quantity usage
  const [quantityUsed, setQuantityUsed] = useState<Map<string, number>>(
    new Map()
  );

  // State for updated inventory items after deduction
  const [updatedInventoryItems, setUpdatedInventoryItems] = useState<
    any[] | null
  >(null);

  // Handler for tracking quantity usage
  const handleQuantityUsed = useCallback(
    (inventoryId: string, quantityUsed: number) => {
      setQuantityUsed((prev) => {
        const newMap = new Map(prev);
        const currentUsed = newMap.get(inventoryId) || 0;
        newMap.set(inventoryId, currentUsed + quantityUsed);
        return newMap;
      });
    },
    []
  );

  // Handler to clear updated inventory items after they've been used
  const handleInventoryUpdated = useCallback(() => {
    setUpdatedInventoryItems(null);
  }, []);

  // Function to validate quantities before saving
  const validateQuantities = useCallback(async () => {
    if (quantityUsed.size === 0 || !projectData?.company_id) {
      return { isValid: true, errors: [] };
    }

    try {
      // Get current inventory to validate quantities
      const inventory = await apiClient.listInventory(projectData.company_id);
      const inventoryMap = new Map();

      if (Array.isArray(inventory)) {
        inventory.forEach((item: any) => {
          inventoryMap.set(item.inventory_id, item);
        });
      }

      const errors: string[] = [];

      // Check each used quantity against current inventory
      for (const [inventoryId, usedQuantity] of quantityUsed.entries()) {
        const inventoryItem = inventoryMap.get(inventoryId);
        if (!inventoryItem) {
          errors.push(`Inventory item ${inventoryId} not found`);
          continue;
        }

        const currentQuantity = inventoryItem.quantity || 0;
        if (currentQuantity < usedQuantity) {
          const itemName =
            inventoryItem.name || inventoryItem.item_id || inventoryId;
          errors.push(
            `${itemName}: Insufficient quantity. Available: ${currentQuantity}, Required: ${usedQuantity}`
          );
        }
      }

      return { isValid: errors.length === 0, errors };
    } catch (error) {
      console.error("Error validating quantities:", error);
      return {
        isValid: false,
        errors: ["Failed to validate inventory quantities"],
      };
    }
  }, [quantityUsed, projectData?.company_id]);

  // Function to update active panel and save to backend
  const updateActivePanel = useCallback(
    async (newActivePanel: ActivePanelType) => {
      if (newActivePanel === activePanel) return;

      setActivePanel(newActivePanel);

      // Update project data locally
      if (projectData) {
        const updatedProjectData = {
          ...projectData,
          activePanel: newActivePanel,
        };
        setProjectData(updatedProjectData);
      }

      // Save to backend if this is not a new project
      if (projectData && projectData.id !== "new") {
        try {
          const response = await apiClient.updateProject(projectData.id, {
            active_panel: newActivePanel,
          });
          console.log("active panel response: ", response);
          // if (!response.ok) {
          //   console.error(
          //     "Failed to save active panel:",
          //     response.status,
          //     response.statusText
          //   );
          // }
        } catch (error) {
          console.error("Error saving active panel:", error);
        }
      }
    },
    [activePanel, projectData, API_URL]
  );

  const {
    boundaryPoints,
    // setBoundaryPoints,
    isBoundaryMode,
    // selectedBoundaryPoint,
    // setSelectedBoundaryPoint,
    handleBoundaryModeToggle,
    handleBoundaryClick,
    handleBoundaryMarkerDragEnd,
    handleBoundaryMarkerClick,
  } = useBoundaryLogic({ projectData, setProjectData });

  const {
    updateProjectObjects,
    handleObjectPlacementStart,
    placedObjects,
    setPlacedObjects,
    isPlacingObject,
    objectToPlace,
    selectedObject,
    setSelectedObject,
    handleObjectClick,
    handleObjectSelect,
    onMarkerDragEnd,
  } = useObjectLogic({
    projectData,
    setProjectData,
    projectId,
    onQuantityUsed: handleQuantityUsed,
  });

  const {
    viewport,
    setViewport,
    isMapMoving,
    handleResetMapPosition,
    handleMapMove,
    handleMapMoveStart,
    handleMapMoveEnd,
  } = useMapViewportLogic({ projectData, setProjectData, lastSaveProject });

  const handleConfirmLeave = () => {
    if (projectId === "new") {
      // Clear unsaved project from localStorage
      localStorage.removeItem("unsavedProject");
    }
    setShowConfirmDialog(false);
    navigate("/dashboard");
  };

  const handleCancelLeave = () => {
    setShowConfirmDialog(false);
  };

  const loadProjectData = async (isMounted: boolean = true) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      console.log("Already loading project data, skipping...");
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingProject(true);
    console.log("loadProjectData effect running. ProjectID:", projectId);

    if (projectId === "new") {
      let initialData: ProjectData;
      const savedProjectJSON = localStorage.getItem("unsavedProject");

      if (savedProjectJSON) {
        initialData = JSON.parse(savedProjectJSON);
      } else {
        const locationState = location.state as PinLocation;
        if (!locationState) {
          console.error(
            "PropertyMap loaded for a new project without location state!"
          );
          if (isMounted) {
            setIsLoadingProject(false);
          }
          isLoadingRef.current = false;
          return;
        }
        // Extract company data from location state if present
        const { company, ...locationWithoutCompany } = locationState;
        const company_id = company?.company_id || "";

        initialData = {
          id: "new",
          name: t("propertyMap.untitledProject"),
          company_id: company_id,
          location: {
            ...locationWithoutCompany,
            zoom: locationWithoutCompany.zoom || 18,
            bearing: locationWithoutCompany.bearing || 0,
            pitch: locationWithoutCompany.pitch || 0,
          },
          objects: [],
          lastModified: "",
          status: "active",
          activePanel: "boundary",
        };
      }

      if (isMounted) {
        setProjectData(initialData);
        setLastSaveProject(initialData);
        setPlacedObjects(initialData.objects || []);

        // Set viewport from saved location data
        if (initialData.location) {
          const initialViewport = {
            latitude: initialData.location.latitude,
            longitude: initialData.location.longitude,
            zoom: initialData.location.zoom || 16,
            bearing: initialData.location.bearing || 0,
            pitch: initialData.location.pitch || 0,
          };
          setViewport(initialViewport);
        }

        // Load company data if company_id exists
        if (initialData.company_id) {
          loadCompanyData(initialData.company_id);
        }

        setIsLoadingProject(false);
      }
      isLoadingRef.current = false;
      return;
    }

    if (!projectId) {
      if (isMounted) {
        setIsLoadingProject(false);
      }
      isLoadingRef.current = false;
      return;
    }

    try {
      const data = await apiClient.getProject(projectId);

      const project_data: ProjectData = transformBackendToFrontend(data);

      if (isMounted) {
        setProjectData(project_data);
        setLastSaveProject(project_data);
        setPlacedObjects(project_data.objects || []);
        if (project_data.location) {
          const initialViewport = {
            latitude: project_data.location.latitude,
            longitude: project_data.location.longitude,
            zoom: project_data.location?.zoom || 16,
            bearing: project_data.location?.bearing || 0,
            pitch: project_data.location?.pitch || 0,
          };
          setViewport(initialViewport);
        }

        // Load company data if company_id exists
        if (project_data.company_id) {
          loadCompanyData(project_data.company_id);
        }
      }
    } catch (error) {
      console.error("Error fetching project data:", error);

      // Determine the type of error and provide appropriate message
      let errorMessage: string;

      if (error instanceof Error) {
        // Check if it's a network error (fetch failed)
        if (error.name === "TypeError" && error.message.includes("fetch")) {
          errorMessage = t("propertyMap.errors.networkError");
        } else {
          errorMessage = error.message;
        }
      } else {
        errorMessage = t("propertyMap.errors.unexpectedError");
      }

      if (isMounted) {
        setError(errorMessage);
        setIsError(true);
      }
    } finally {
      if (isMounted) {
        setIsLoadingProject(false);
      }
      isLoadingRef.current = false;
    }
  };

  // Retry loading project
  const retryLoadProject = () => {
    clearMessages(setError, setIsError);
    // Reset loading state and trigger the useEffect again
    setIsLoadingProject(true);
    isLoadingRef.current = false;

    loadProjectData();
  };

  // Load company data
  const loadCompanyData = async (companyId: string) => {
    if (!companyId) {
      console.warn("No company ID provided, skipping company fetch");
      return;
    }

    try {
      const company = await apiClient.getCompany(companyId);
      if (company) {
        setCompanyInfo({
          name: company.name || "Unknown Company",
          company_id: company.company_id || companyId,
        });
      }
    } catch (error) {
      console.error("Error loading company data:", error);
      // Set fallback company info
      setCompanyInfo({
        name: "Unknown Company",
        company_id: companyId,
      });
    }
  };

  // Add state to track if there are unsaved changes

  // Add confirmation modal state
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const hasUnsavedChanges = useCallback(() => {
    // Check if projectData and lastSaveProject are logically equivalent
    if (projectData === null || lastSaveProject === null) {
      return false;
    }

    // Create copies for comparison, excluding fields that shouldn't trigger unsaved changes
    const { activePanel: _, ...projectDataForComparison } = projectData;
    const { activePanel: __, ...lastSaveProjectForComparison } =
      lastSaveProject;

    if (projectData?.boundaryPoints && boundaryPoints) {
      if (
        JSON.stringify(projectData?.boundaryPoints) !==
        JSON.stringify(boundaryPoints)
      ) {
        return true;
      }
    }

    // Compare relevant fields for logical equivalence
    return (
      JSON.stringify(projectDataForComparison) !==
      JSON.stringify(lastSaveProjectForComparison)
    );
  }, [projectData, lastSaveProject, boundaryPoints]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        // Modern browsers require returnValue to be set for beforeunload to work
        event.preventDefault();
        event.returnValue = "";

        // For older browsers
        return t("propertyMap.unsavedChangesWarning");
      }
    };

    // Also handle pagehide event for better mobile support
    const handlePageHide = (event: PageTransitionEvent) => {
      if (hasUnsavedChanges()) {
        // Try to prevent the page from being unloaded
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [hasUnsavedChanges]); // Add hasUnsavedChanges to dependencies

  // Effect to handle incoming location from router state
  useEffect(() => {
    const locationState = location.state as PinLocation;
    if (locationState?.latitude && locationState?.longitude) {
      setViewport({
        latitude: locationState.latitude,
        longitude: locationState.longitude,
        zoom: locationState.zoom || 18,
        bearing: locationState.bearing || 0,
        pitch: locationState.pitch || 0,
      });
    }
  }, [location.state]);

  // Sync projectData with placedObjects and update lastSaveProject when objects change
  useEffect(() => {
    updateProjectObjects();
  }, [placedObjects]);

  // Track unsaved changes and show warning
  useEffect(() => {
    const hasChanges = hasUnsavedChanges();
    setShowUnsavedWarning(hasChanges);

    // Update browser tab title to indicate unsaved changes
    const projectName = projectData?.name || t("propertyMap.untitledProject");
    if (hasChanges) {
      document.title = `*${projectName} - Gesslar Gardens`;
    } else {
      document.title = `${projectName} - Gesslar Gardens`;
    }

    if (projectData?.activePanel && projectData.activePanel !== activePanel) {
      updateActivePanel(projectData.activePanel);
    }
  }, [projectData, lastSaveProject, boundaryPoints, hasUnsavedChanges]);

  useEffect(() => {
    if (projectData?.activePanel && projectData.activePanel !== activePanel) {
      setProjectData({ ...projectData, activePanel: activePanel });
    }
  }, [activePanel]);

  // Keyboard shortcuts for save (Ctrl+S or Cmd+S)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        if (hasUnsavedChanges() && !isSaving) {
          handleSaveClick();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasUnsavedChanges, isSaving]);

  // Cleanup effect to reset title when component unmounts
  useEffect(() => {
    return () => {
      document.title = "Gesslar Gardens";
    };
  }, []);

  // Auto-clear success messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Load project data from API or local storage
  useEffect(() => {
    let isMounted = true;

    loadProjectData(isMounted);

    // Cleanup function
    return () => {
      isMounted = false;
      isLoadingRef.current = false;
    };
  }, [projectId]);

  // Check for success message from localStorage after project loads
  useEffect(() => {
    if (projectData && projectId !== "new") {
      const savedSuccessMessage = localStorage.getItem("projectSuccessMessage");
      if (savedSuccessMessage) {
        setSuccessMessage(savedSuccessMessage);
        localStorage.removeItem("projectSuccessMessage");
      }
    }
  }, [projectData, projectId]);

  // Auto-save to local storage for new, unsaved projects
  useEffect(() => {
    if (projectId === "new" && projectData) {
      const updatedProjectData = {
        ...projectData,
        objects: placedObjects,
        boundaryPoints: boundaryPoints,
        location: {
          ...projectData.location,
          zoom: viewport.zoom,
          bearing: viewport.bearing,
          pitch: viewport.pitch,
        },
      };
      localStorage.setItem(
        "unsavedProject",
        JSON.stringify(updatedProjectData)
      );
    }
  }, [placedObjects, boundaryPoints, projectData, projectId, viewport]);

  const handleHomeClick = () => {
    if (projectId === "new") {
      setConfirmMessage(t("propertyMap.discardUnsavedProject"));
      setShowConfirmDialog(true);
    } else if (hasUnsavedChanges()) {
      setConfirmMessage(t("propertyMap.leaveWithoutSaving"));
      setShowConfirmDialog(true);
    } else {
      localStorage.removeItem("unsavedProject");
      navigate("/dashboard");
    }
  };

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (isBoundaryMode) {
        const { lng, lat } = event.lngLat;
        handleBoundaryClick(lng, lat);
      } else if (isPlacingObject && objectToPlace) {
        const { lng, lat } = event.lngLat;
        handleObjectClick(lng, lat);
      } else if (!isBoundaryMode) {
        setSelectedObject(null);
      }
    },
    [
      isBoundaryMode,
      boundaryPoints,
      isPlacingObject,
      objectToPlace,
      projectId,
      projectData,
    ]
  );

  const saveProject = async () => {
    if (isSaving) {
      console.log("Save already in progress, ignoring request");
      return;
    }

    setIsSaving(true);

    try {
      //projectData has all project data
      console.log("Saving project data: ", projectData);

      if (
        projectData?.id == null ||
        projectData?.id == undefined ||
        projectData?.id == ""
      ) {
        console.error("Cannot save a null project");
        return;
      }

      // First, validate and deduct inventory quantities if there are any used quantities
      if (quantityUsed.size > 0 && projectData?.company_id) {
        // Validate quantities before attempting to deduct
        const validation = await validateQuantities();
        if (!validation.isValid) {
          setSaveError(
            `Inventory validation failed:\n${validation.errors.join("\n")}`
          );
          return;
        }

        try {
          // Convert quantityUsed Map to array format for API
          const deductions = Array.from(quantityUsed.entries()).map(
            ([inventoryId, quantity]) => ({
              inventory_id: inventoryId,
              quantity_to_deduct: quantity,
            })
          );

          const deductionsData = {
            deductions: deductions,
          };

          // Deduct quantities from inventory
          const updated_items = await apiClient.deductInventoryQuantities(
            projectData.company_id,
            deductionsData
          );

          // Store the updated inventory items for the ObjectsPanel
          setUpdatedInventoryItems(updated_items);
          console.log(
            "Stored updated inventory items for ObjectsPanel:",
            updated_items
          );

          // Clear the used quantities after successful deduction
          setQuantityUsed(new Map());

          console.log(
            "Successfully deducted inventory quantities:",
            deductions
          );
        } catch (error: any) {
          console.error("Error deducting inventory quantities:", error);
          setSaveError(`Failed to update inventory: ${error.message}`);
          return; // Don't proceed with project save if inventory update fails
        }
      }

      if (projectData.id == "new") {
        // Prepare project data based on saveMapView setting
        const projectDataToSave = {
          ...projectData,
          location: {
            ...projectData.location,
            ...(saveMapView
              ? {
                  zoom: viewport.zoom,
                  bearing: viewport.bearing,
                  pitch: viewport.pitch,
                }
              : {}),
          },
        };

        const res = await apiClient.createProject(
          transformFrontendToBackend(projectDataToSave)
        );

        console.log("res: ", res);

        if (res.project_id) {
          const responseData = res;

          const newProjectId = responseData.project_id;
          if (newProjectId) {
            // Store success message in localStorage for after redirect
            localStorage.setItem(
              "projectSuccessMessage",
              "Project created successfully!"
            );
            // Navigate to the new project page
            setShowSaveConfirmation(false);
            navigate(`/projects/${newProjectId}/map`);
          } else {
            console.error("No valid project ID returned from server");
            setShowSaveConfirmation(false);
          }
        } else {
          console.error("Failed to save project:", res.status, res.statusText);

          // Try to get error details from response
          let errorMessage = "Failed to save project";
          try {
            const errorData = await res.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) {
            // If we can't parse the response, use status text
            errorMessage = res.statusText || errorMessage;
          }

          setSaveError(errorMessage);
          return; // Don't close modal, let user see the error
        }
      } else {
        // Update project

        // Prepare project data based on saveMapView setting
        const projectDataToSave = {
          ...projectData,
          location: {
            ...projectData.location,
            ...(saveMapView
              ? {
                  zoom: viewport.zoom,
                  bearing: viewport.bearing,
                  pitch: viewport.pitch,
                }
              : {}),
          },
        };
        try {
          await apiClient.updateProject(
            projectDataToSave.id,
            transformFrontendToBackend(projectDataToSave)
          );

          setShowSaveConfirmation(false);
          setLastSaveProject(projectData);
          setProjectData(projectData);
          setSuccessMessage("Project updated successfully!");

          setShowSaveConfirmation(false);
        } catch (error: any) {
          setSaveError(error.message);
          return; // Don't close modal, let user see the error
        }
      }
    } catch (error) {
      console.error("Error saving project:", error);

      // Handle network or other errors
      let errorMessage = t("propertyMap.errors.saveError");
      if (error instanceof Error) {
        if (error.name === "TypeError" && error.message.includes("fetch")) {
          errorMessage = t("propertyMap.errors.networkError");
        } else {
          errorMessage = error.message;
        }
      }

      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    setShowSaveConfirmation(true);
    setSaveError(null); // Clear any previous errors when opening the modal
  };

  const handleConfirmSave = () => {
    saveProject();
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
    setSaveError(null); // Clear error when canceling
  };

  const handlePropertyLocationDragEnd = useCallback(
    async (event: MarkerDragEvent) => {
      if (!projectData) return;

      const { lng, lat } = event.lngLat;

      // Update the project data with new location
      const updatedProjectData: ProjectData = {
        ...projectData,
        location: {
          ...projectData.location,
          longitude: lng,
          latitude: lat,
        },
      };

      setProjectData(updatedProjectData);

      // Update viewport to center on new location
      setViewport({
        latitude: lat,
        longitude: lng,
        zoom: viewport.zoom || 16,
        bearing: viewport.bearing || 0,
        pitch: viewport.pitch || 0,
      });
    },
    [projectData, projectId, viewport]
  );

  const mapStyle = {
    width: "100%",
    height: "100%",
    cursor: isBoundaryMode ? "crosshair" : isPlacingObject ? "copy" : "grab",
    objectFit: "cover" as const,
  };

  // Handle project name editing
  const handleNameEdit = () => {
    setIsEditingName(true);
    setEditingName(projectData?.name || "");
  };

  const handleNameSave = () => {
    if (projectData) {
      const updatedProjectData = {
        ...projectData,
        name: editingName.trim() || t("propertyMap.untitledProject"),
      };
      setProjectData(updatedProjectData);

      // Save to localStorage for new projects
      if (projectId === "new") {
        localStorage.setItem(
          "unsavedProject",
          JSON.stringify(updatedProjectData)
        );
      }
    }
    setIsEditingName(false);
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditingName(projectData?.name || "");
  };

  // Handle address editing
  const handleAddressEdit = () => {
    setIsEditingAddress(true);
    setEditingAddress(projectData?.location?.address || "");
  };

  const handleAddressSave = (newAddr?: string) => {
    if (projectData) {
      if (newAddr == undefined) {
        newAddr = editingAddress;
      }

      const updatedProjectData = {
        ...projectData,
        location: {
          ...projectData.location,
          address: newAddr.trim(),
        },
      };
      setProjectData(updatedProjectData);

      // Save to localStorage for new projects
      if (projectId === "new") {
        localStorage.setItem(
          "unsavedProject",
          JSON.stringify(updatedProjectData)
        );
      }
    }
  };

  // Handle address autocomplete selection
  const handleAddressRetrieve = (res: any) => {
    if (!res.features || res.features.length === 0) {
      console.log("No features found in autocomplete result");
      return;
    }

    const feature = res.features[0];
    const address = feature.properties.full_address || feature.place_name;
    setEditingAddress(address);
    handleAddressSave(address);
    // Don't save immediately - let user edit further if they want
  };

  // Handle key presses for editing
  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleNameSave();
    } else if (e.key === "Escape") {
      handleNameCancel();
    }
  };

  // Update function for the plant table to include image thumbnail column
  return (
    <div className="property-map-page">
      <Header />
      <div className="property-map-container">
        {/* Success Message Banner */}
        {successMessage && (
          <div className="success-message-banner">
            <div className="success-content">
              {/* <span className="success-icon">✔️</span> */}
              <span className="success-text">{successMessage}</span>
              <button
                className="map-success-close-button"
                onClick={() => setSuccessMessage(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {isLoadingProject ? (
          <div className="loading-screen">
            <div className="loading-content">
              <div className="spinner"></div>
              <h2>Loading Project...</h2>
              <p>Please wait while we load your project data.</p>
            </div>
          </div>
        ) : isError ? (
          <div className="error-screen">
            <div className="error-content">
              <div className="error-icon">⚠️</div>
              <h2>Error Loading Project</h2>
              <p className="error-message">{error}</p>
              <div className="error-actions">
                <button
                  className="retry-button"
                  onClick={retryLoadProject}
                  disabled={isLoadingProject}
                >
                  {isLoadingProject ? "Retrying..." : "Try Again"}
                </button>
                <button
                  className="back-to-dashboard-button"
                  onClick={() => navigate("/dashboard")}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Unsaved Changes Warning Banner */}
            {showUnsavedWarning && (
              <div className="unsaved-warning-banner">
                <div className="warning-content">
                  <span className="warning-icon">⚠️</span>
                  <span className="warning-text">
                    {t("propertyMap.unsavedChangesWarningText")}
                  </span>
                  <div className="map-unsaved-warning-actions">
                    <button className="leave-button" onClick={handleHomeClick}>
                      {t("propertyMap.leaveAnyway")}
                    </button>
                    <button
                      className="save-now-button"
                      onClick={handleSaveClick}
                      disabled={isSaving}
                    >
                      {isSaving ? t("common.saving") : t("propertyMap.saveNow")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="map-wrapper">
              <MapboxMap
                {...viewport}
                ref={mapRef}
                onMove={handleMapMove}
                onMoveStart={handleMapMoveStart}
                onMoveEnd={handleMapMoveEnd}
                onClick={handleMapClick}
                style={mapStyle}
                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN || ""}
                onError={(e) => console.error("Map error:", e)}
                cursor={
                  isMapMoving
                    ? "grabbing"
                    : isPlacingObject
                    ? "crosshair"
                    : "grab"
                }
              >
                <ObjectMarkers
                  placedObjects={placedObjects}
                  selectedObject={selectedObject}
                  isBoundaryMode={isBoundaryMode}
                  onDragEnd={onMarkerDragEnd}
                  onSelect={handleObjectSelect}
                />

                <BoundaryMarkers
                  boundaryPoints={boundaryPoints}
                  isBoundaryMode={isBoundaryMode}
                  onDragEnd={handleBoundaryMarkerDragEnd}
                  onClick={handleBoundaryMarkerClick}
                />

                <BoundaryPolygon
                  boundaryPoints={boundaryPoints}
                  isBoundaryMode={isBoundaryMode}
                />

                {/* Property Location Marker */}
                {projectData?.location && (
                  <Marker
                    longitude={projectData.location.longitude}
                    latitude={projectData.location.latitude}
                    anchor="bottom"
                    draggable={!isBoundaryMode}
                    onDragEnd={handlePropertyLocationDragEnd}
                  >
                    <div
                      className={`property-location-marker ${
                        isBoundaryMode ? "boundary-mode-disabled" : ""
                      }`}
                      title={
                        isBoundaryMode
                          ? "Property location cannot be moved in boundary mode"
                          : "Drag to move property location"
                      }
                    >
                      <span
                        role="img"
                        aria-label="Property Location"
                        style={{
                          fontSize: "20px",
                          opacity: isBoundaryMode ? 0.5 : 1,
                        }}
                      >
                        ⌾
                      </span>
                    </div>
                  </Marker>
                )}

                <Source
                  id="mapbox-dem"
                  type="raster-dem"
                  url="mapbox://mapbox.mapbox-terrain-dem-v1"
                  tileSize={512}
                  maxzoom={14}
                />
                <Layer {...skyLayer} />
              </MapboxMap>
            </div>

            <div className="project-info-panel">
              <div className="project-header">
                {projectId === "new" ? (
                  <button
                    className="back-button danger"
                    onClick={handleHomeClick}
                    title={t("propertyMap.discardUnsavedProject")}
                  >
                    ✕
                  </button>
                ) : hasUnsavedChanges() ? (
                  <button
                    className="back-button warning"
                    onClick={handleHomeClick}
                    title={t("propertyMap.leaveWithUnsavedChanges")}
                  >
                    ✕
                  </button>
                ) : (
                  <button
                    className="back-button default"
                    onClick={handleHomeClick}
                    title={t("propertyMap.goToDashboard")}
                  >
                    ←
                  </button>
                )}

                <div className="project-title-section">
                  {isEditingName ? (
                    <div className="editable-field">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={handleNameKeyPress}
                        onBlur={handleNameSave}
                        className="editable-input"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <h2
                      onClick={handleNameEdit}
                      className="editable-text"
                      title={t("propertyMap.clickToEditProjectName")}
                    >
                      {projectData?.name || t("propertyMap.loadingProject")}
                    </h2>
                  )}
                </div>
              </div>

              {isEditingAddress ? (
                <div className="editable-field">
                  <div className="address-input-container">
                    {/* @ts-ignore */}
                    <AddressAutofill
                      accessToken={import.meta.env.VITE_MAPBOX_TOKEN || ""}
                      onRetrieve={handleAddressRetrieve}
                      options={{
                        streets: false, // Only return addresses, not POIs
                        language: "en",
                        limit: 5,
                      }}
                    >
                      <input
                        name="address"
                        value={editingAddress}
                        onChange={(e) => {
                          setEditingAddress(e.target.value);
                          handleAddressSave(e.target.value);
                        }}
                        className="editable-input"
                        autoFocus
                        placeholder={t("propertyMap.addressPlaceholder")}
                        autoComplete="street-address"
                        onBlur={(e) => {
                          setEditingAddress(e.target.value);
                          handleAddressSave(e.target.value);
                        }} // When user clicks off the textbox
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setIsEditingAddress(false);
                          }
                        }}
                      />
                    </AddressAutofill>
                  </div>
                </div>
              ) : (
                <p
                  onClick={handleAddressEdit}
                  className="editable-text"
                  title={t("propertyMap.clickToEditProjectAddress")}
                >
                  {projectData?.location?.address || t("propertyMap.clickToAddAddress")}
                </p>
              )}

              {/* Save indicator */}
              {hasUnsavedChanges() && (
                <div className="save-indicator">
                  <span className="save-indicator-text">
                    Don't forget to save your changes!
                  </span>
                </div>
              )}

              {/* Reset Map Position Button */}
              {lastSaveProject?.location && (
                <div className="reset-position-section">
                  <button
                    className={`reset-position-btn ${
                      isMapMoving ? "map-moving" : ""
                    }`}
                    onClick={handleResetMapPosition}
                    title={
                      isMapMoving
                        ? "Map is moving, reset will wait for movement to stop"
                        : "Reset map to last saved position"
                    }
                    disabled={isMapMoving}
                  >
                    <span role="img" aria-label={t("propertyMap.resetPosition")}>
                      ↺
                    </span>
                    <span className="reset-btn-text">
                      {isMapMoving ? t("propertyMap.mapMoving") : t("propertyMap.resetMapPosition")}
                    </span>
                  </button>
                </div>
              )}
            </div>

            <SidePanel
              activePanel={activePanel}
              updateActivePanel={updateActivePanel}
              propertyBoundaryPanelProps={{
                onBoundaryModeToggle: handleBoundaryModeToggle,
                boundaryPoints: boundaryPoints,
                isBoundaryMode: isBoundaryMode,
              }} // update placedObjects, quantityUsed,
              objectsPanelProps={{
                onCatalogObjectSelect: handleObjectPlacementStart,
                onPlacedObjectSelect: handleObjectSelect,
                placedObjects: placedObjects,
                selectedObjectId: selectedObject?.id,
                saveProject: handleSaveClick,
                hasUnsavedChanges: hasUnsavedChanges,
                isSaving: isSaving,
                companyId: projectData?.company_id || "",
                onQuantityUsed: handleQuantityUsed,
                quantityUsed: quantityUsed,
                objectToPlace: objectToPlace,
                updatedInventoryItems: updatedInventoryItems,
                onInventoryUpdated: handleInventoryUpdated,
              }}
              companyInfo={companyInfo}
            />
          </>
        )}

        {/* Save Confirmation Modal */}
        {showSaveConfirmation && (
          <div className="modal-overlay">
            <div className="save-confirmation-modal">
              <div className="modal-header">
                <h3>{t("propertyMap.saveProject")}</h3>
              </div>
              <div className="modal-content">
                <p>{t("propertyMap.confirmSaveProject")}</p>
                <div className="project-summary">
                  <p>
                    <strong>{t("propertyMap.projectName")}:</strong>{" "}
                    {projectData?.name || t("propertyMap.untitled")}
                  </p>
                  <p>
                    <strong>{t("propertyMap.address")}:</strong>{" "}
                    {projectData?.location?.address || t("propertyMap.noAddressSet")}
                  </p>
                  <p>
                    <strong>{t("propertyMap.objectsPlaced")}:</strong> {placedObjects.length}
                  </p>
                </div>

                <div className="save-options">
                  <label className="save-option-checkbox">
                    <input
                      type="checkbox"
                      checked={saveMapView}
                      onChange={(e) => setSaveMapView(e.target.checked)}
                    />
                    <span className="checkbox-label">
                      {t("propertyMap.saveCurrentMapView")} ({t("propertyMap.zoomBearingPitch")})
                    </span>
                  </label>
                </div>

                {/* Error message display */}
                {saveError && (
                  <div className="save-error-message">
                    <div className="error-icon">⚠️</div>
                    <div className="error-text">{saveError}</div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  className="confirm-save-btn"
                  onClick={handleConfirmSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner"></span>
                      {t("common.saving")}
                    </>
                  ) : (
                    t("propertyMap.saveProject")
                  )}
                </button>
                <button
                  className="cancel-save-btn"
                  onClick={handleCancelSave}
                  disabled={isSaving}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="unsaved-modal-overlay">
          <div className="confirm-dialog">
            <div className="modal-header">
              <h3>Confirm Action</h3>
            </div>
            <div className="modal-content">
              <p>{confirmMessage}</p>
            </div>
            <div className="modal-actions">
              <button className="confirm-btn" onClick={handleConfirmLeave}>
                {t("common.continue")}
              </button>
              <button className="cancel-btn" onClick={handleCancelLeave}>
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;
